import React, { useEffect, useState } from 'react';
import {
    Box, Typography, Card, CardContent, TextField, Button, Grid, Alert,
    Divider, Chip, FormControl, InputLabel, Select, MenuItem
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import BusinessIcon from '@mui/icons-material/Business';
import api from '../services/api';
import { useAuth } from '../contexts/AuthContext';

const UF_LIST = ['AC', 'AL', 'AM', 'AP', 'BA', 'CE', 'DF', 'ES', 'GO', 'MA', 'MG', 'MS', 'MT', 'PA', 'PB', 'PE', 'PI', 'PR', 'RJ', 'RN', 'RO', 'RR', 'RS', 'SC', 'SE', 'SP', 'TO'];

const PLAN_LABELS = { trial: 'Trial (30 dias)', basic: 'Básico', professional: 'Profissional', enterprise: 'Enterprise' };

const SettingsPage = () => {
    const { tenant, loadUser, isAdmin } = useAuth();
    const [form, setForm] = useState({ name: '', cnpj: '', phone: '', settings: { state: '', city: '', defaultRegion: '' } });
    const [success, setSuccess] = useState('');
    const [error, setError] = useState('');
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (tenant) {
            setForm({
                name: tenant.name || '',
                cnpj: tenant.cnpj || '',
                phone: tenant.phone || '',
                settings: { state: tenant.settings?.state || '', city: tenant.settings?.city || '', defaultRegion: tenant.settings?.defaultRegion || '' }
            });
        }
    }, [tenant]);

    const set = (field) => (e) => setForm(f => ({ ...f, [field]: e.target.value }));
    const setSetting = (field) => (e) => setForm(f => ({ ...f, settings: { ...f.settings, [field]: e.target.value } }));

    const handleSave = async () => {
        setSaving(true); setError(''); setSuccess('');
        try {
            await api.put('/tenants/me', form);
            await loadUser();
            setSuccess('Configurações salvas com sucesso!');
        } catch (err) {
            setError(err.response?.data?.message || 'Erro ao salvar configurações');
        } finally { setSaving(false); }
    };

    return (
        <Box>
            <Typography variant="h4" fontWeight={700} mb={0.5}>Configurações</Typography>
            <Typography color="text.secondary" mb={3}>Gerencie as configurações da sua organização</Typography>

            {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>{success}</Alert>}
            {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

            <Grid container spacing={3}>
                {/* Org data */}
                <Grid item xs={12} md={8}>
                    <Card>
                        <CardContent>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3 }}>
                                <BusinessIcon color="primary" />
                                <Typography variant="h6" fontWeight={600}>Dados da Organização</Typography>
                            </Box>
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                <TextField label="Nome da organização" value={form.name} onChange={set('name')} fullWidth disabled={!isAdmin()} />
                                <Grid container spacing={2}>
                                    <Grid item xs={12} sm={6}>
                                        <TextField label="CNPJ" value={form.cnpj} onChange={set('cnpj')} fullWidth placeholder="00.000.000/0001-00" disabled={!isAdmin()} />
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <TextField label="Telefone" value={form.phone} onChange={set('phone')} fullWidth disabled={!isAdmin()} />
                                    </Grid>
                                </Grid>
                                <Divider />
                                <Typography variant="subtitle2" fontWeight={600} color="text.secondary">Localização padrão</Typography>
                                <Grid container spacing={2}>
                                    <Grid item xs={12} sm={4}>
                                        <FormControl fullWidth>
                                            <InputLabel>Estado (UF)</InputLabel>
                                            <Select value={form.settings.state} onChange={setSetting('state')} label="Estado (UF)" disabled={!isAdmin()}>
                                                <MenuItem value="">Não definido</MenuItem>
                                                {UF_LIST.map(uf => <MenuItem key={uf} value={uf}>{uf}</MenuItem>)}
                                            </Select>
                                        </FormControl>
                                    </Grid>
                                    <Grid item xs={12} sm={4}>
                                        <TextField label="Município" value={form.settings.city} size="large" onChange={setSetting('city')} fullWidth disabled={!isAdmin()} />
                                    </Grid>
                                    <Grid item xs={12} sm={4}>
                                        <FormControl fullWidth>
                                            <InputLabel>Região padrão</InputLabel>
                                            <Select value={form.settings.defaultRegion} onChange={setSetting('defaultRegion')} label="Região padrão" disabled={!isAdmin()}>
                                                {UF_LIST.map(uf => <MenuItem key={uf} value={uf}>{uf}</MenuItem>)}
                                            </Select>
                                        </FormControl>
                                    </Grid>
                                </Grid>
                                {isAdmin() && (
                                    <Button variant="contained" startIcon={<SaveIcon />} onClick={handleSave} disabled={saving} sx={{ alignSelf: 'flex-start', mt: 1 }}>
                                        {saving ? 'Salvando...' : 'Salvar configurações'}
                                    </Button>
                                )}
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>

                {/* Plan info */}
                <Grid item xs={12} md={4} sx={{ minWidth: 280 }}>
                    <Card>
                        <CardContent>
                            <Typography variant="h6" fontWeight={600} mb={2}>Plano atual</Typography>
                            <Chip label={PLAN_LABELS[tenant?.plan] || tenant?.plan || 'Trial'} color="primary" sx={{ mb: 2 }} />
                            <Divider sx={{ my: 2 }} />
                            <Typography variant="body2" color="text.secondary" mb={1}>Limites do plano</Typography>
                            {[
                                ['Usuários', tenant?.limits?.usersMax],
                                ['Pesquisas/mês', tenant?.limits?.searchesPerMonth],
                                ['Listas', tenant?.limits?.listsMax]
                            ].map(([label, val]) => (
                                <Box key={label} sx={{ display: 'flex', justifyContent: 'space-between', py: 0.75, borderBottom: '1px solid #f0f0f0' }}>
                                    <Typography variant="body2" color="text.secondary">{label}</Typography>
                                    <Typography variant="body2" fontWeight={600}>{val ?? '∞'}</Typography>
                                </Box>
                            ))}
                            <Divider sx={{ my: 2 }} />
                            <Typography variant="body2" color="text.secondary" mb={1}>Fontes de dados</Typography>
                            {['Compras.gov.br', 'PNCP', 'PNCP - Atas de RP'].map(s => (
                                <Box key={s} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                                    <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#2e7d32' }} />
                                    <Typography variant="body2">{s}</Typography>
                                </Box>
                            ))}
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>
        </Box>
    );
};

export default SettingsPage;
